import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trash2, Send, ChevronDown, CalendarPlus, DollarSign, Search, CheckCircle2 } from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const inputCls =
  "bg-[#0a1428] border border-white/15 px-3 py-2 text-sm text-white focus:outline-none focus:border-[#2a5fdb]";

const fmtTime = (t) => {
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, "0")} ${ampm}`;
};

export default function Admin() {
  const [key, setKey] = useState(sessionStorage.getItem("c2c_admin_key") || "");
  const [authed, setAuthed] = useState(false);

  return (
    <main data-testid="admin-root" className="min-h-screen px-6 md:px-12 py-20 bg-[#060b14] text-white">
      <div className="max-w-[1400px] mx-auto">
        <div className="chapter-num mb-6">⟶ Admin</div>
        <h1 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] tracking-tight">
          Coach panel
        </h1>
        {!authed ? (
          <AdminLogin savedKey={key} onAuthed={(k) => { setKey(k); setAuthed(true); }} />
        ) : (
          <AdminPanel adminKey={key} />
        )}
      </div>
    </main>
  );
}

const AdminLogin = ({ savedKey, onAuthed }) => {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [checking, setChecking] = useState(!!savedKey);

  const tryLogin = async (password, silent) => {
    try {
      await api.post("/admin/login", { password });
      sessionStorage.setItem("c2c_admin_key", password);
      onAuthed(password);
    } catch {
      if (!silent) setErr("Wrong password.");
      sessionStorage.removeItem("c2c_admin_key");
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (savedKey) tryLogin(savedKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (checking) return <p className="mt-12 text-white/50">Checking…</p>;

  return (
    <form
      className="mt-12 max-w-sm space-y-4"
      onSubmit={(e) => { e.preventDefault(); tryLogin(pw, false); }}
    >
      <input
        data-testid="admin-password-input"
        type="password"
        className={`${inputCls} w-full py-3 px-4`}
        placeholder="Admin password"
        value={pw}
        onChange={(e) => setPw(e.target.value)}
      />
      {err && <p data-testid="admin-login-error" className="text-red-400 text-sm">{err}</p>}
      <button data-testid="admin-login-submit" type="submit" className="btn-sharp">
        Enter <span aria-hidden>→</span>
      </button>
    </form>
  );
};

const AdminPanel = ({ adminKey }) => {
  const [tab, setTab] = useState("bookings");
  const headers = { "X-Admin-Key": adminKey };
  return (
    <div className="mt-12">
      <div className="flex gap-2 border-b border-white/10 pb-px">
        {["bookings", "requests", "availability", "10x packs", "rep program", "waitlist", "inquiries"].map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t}`}
            onClick={() => setTab(t)}
            className={`px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] border-b-2 transition-colors ${
              tab === t ? "border-[#2a5fdb] text-white" : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === "bookings" && <BookingsTab headers={headers} />}
      {tab === "availability" && <AvailabilityTab headers={headers} />}
      {tab === "10x packs" && <PacksTab headers={headers} />}
      {tab === "rep program" && <RepTab headers={headers} />}
      {tab === "waitlist" && <WaitlistTab headers={headers} />}
      {tab === "inquiries" && <InquiriesTab />}
      {tab === "requests" && <RequestsTab headers={headers} />}
    </div>
  );
};

const money = (c) => `$${((c || 0) / 100).toLocaleString("en-AU")}`;

const PacksTab = ({ headers }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ parent_name: "", player_name: "", phone: "", email: "", notes: "", paid: false, amount: "900", done: "0" });
  const [err, setErr] = useState("");

  const load = async () => {
    const { data: d } = await api.get("/admin/packs", { headers });
    setData(d);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const { amount, done, ...rest } = form;
      await api.post(
        "/admin/packs",
        { ...rest, phone: form.phone || null, email: form.email || null, notes: form.notes || null, price: Math.round(Number(amount || 900) * 100), completed_sessions: Math.min(10, Math.max(0, Number(done || 0))) },
        { headers }
      );
      setForm({ parent_name: "", player_name: "", phone: "", email: "", notes: "", paid: false, amount: "900", done: "0" });
      load();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Failed to add pack.");
    }
  };

  if (!data) return <p className="mt-10 text-white/50">Loading…</p>;
  return (
    <div className="mt-10 space-y-10">
      <div className="grid grid-cols-2 gap-3 max-w-md">
        <StatCard label="Packs paid" value={money(data.revenue_paid)} tid="packs-revenue-paid" accent />
        <StatCard label="Packs unpaid" value={money(data.revenue_outstanding)} tid="packs-revenue-outstanding" />
      </div>

      <div className="border border-white/10 bg-[#0a1428] p-6 max-w-3xl">
        <h3 className="font-display uppercase text-2xl tracking-tight">
          Add a <span className="text-[#2a5fdb]">10x pack</span>
        </h3>
        <p className="mt-2 text-white/60 text-sm">
          For clients who bought a 10-session pack over text ($900 standard —
          adjust the amount if you did a different deal). Their sessions take
          priority — every slot you schedule instantly shows as Booked on the site.
        </p>
        <form onSubmit={add} className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input data-testid="pack-input-parent" className={inputCls + " py-3 px-4"} placeholder="Parent name *" value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} />
          <input data-testid="pack-input-player" className={inputCls + " py-3 px-4"} placeholder="Player name *" value={form.player_name} onChange={(e) => setForm({ ...form, player_name: e.target.value })} />
          <input data-testid="pack-input-phone" className={inputCls + " py-3 px-4"} placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <input data-testid="pack-input-email" className={inputCls + " py-3 px-4"} type="email" placeholder="Email (for session reminders)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input data-testid="pack-input-notes" className={inputCls + " py-3 px-4"} placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <label className="flex flex-col gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
            Amount ($AUD)
            <input data-testid="pack-input-amount" type="number" min="1" className={inputCls + " py-3 px-4"} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </label>
          <label className="flex flex-col gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
            Sessions already done (0–10)
            <input data-testid="pack-input-done" type="number" min="0" max="10" className={inputCls + " py-3 px-4"} value={form.done} onChange={(e) => setForm({ ...form, done: e.target.value })} />
          </label>
          <label className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/70">
            <input data-testid="pack-input-paid" type="checkbox" checked={form.paid} onChange={(e) => setForm({ ...form, paid: e.target.checked })} className="accent-[#2a5fdb] w-4 h-4" />
            $900 already paid
          </label>
          {err && <p className="text-red-400 text-sm sm:col-span-2">{err}</p>}
          <div className="sm:col-span-2">
            <button data-testid="pack-add-btn" type="submit" disabled={!form.parent_name.trim() || !form.player_name.trim()} className="btn-sharp disabled:opacity-40 disabled:cursor-not-allowed">
              Add pack
            </button>
          </div>
        </form>
      </div>

      <div className="space-y-6">
        {data.packs.map((p) => (
          <PackCard key={p.id} pack={p} headers={headers} onChanged={load} />
        ))}
        {data.packs.length === 0 && (
          <p className="text-white/50 font-mono text-xs uppercase tracking-[0.2em]">No packs yet.</p>
        )}
      </div>
    </div>
  );
};

const ExpiryBadge = ({ pack }) => {
  if (pack.days_left == null) {
    return (
      <span
        data-testid={`pack-expiry-${pack.id}`}
        className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-white/15 text-white/40"
      >
        12-wk window starts at first session
      </span>
    );
  }
  const d = pack.days_left;
  const expired = d < 0;
  const soon = d >= 0 && d <= 14;
  const weeks = Math.floor(Math.abs(d) / 7);
  const days = Math.abs(d) % 7;
  const span = weeks > 0 ? `${weeks}w ${days}d` : `${days}d`;
  const endStr = new Date(`${pack.expiry_date}T00:00:00`).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
  });
  return (
    <span
      data-testid={`pack-expiry-${pack.id}`}
      className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${
        expired
          ? "border-red-400/60 text-red-400"
          : soon
          ? "border-amber-400/60 text-amber-400"
          : "border-white/15 text-white/60"
      }`}
    >
      {expired ? `Window ended ${endStr} (${span} ago)` : `${span} left · ends ${endStr}`}
    </span>
  );
};

const PackCard = ({ pack, headers, onChanged }) => {
  const [date, setDate] = useState("");
  const [dayInfo, setDayInfo] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const cancelled = pack.status === "cancelled";

  const genTimes = (windows) => {
    const out = [];
    windows.forEach((w) => {
      const [sh, sm] = w.start.split(":").map(Number);
      const [eh, em] = w.end.split(":").map(Number);
      let t = sh * 60 + sm;
      while (t + 60 <= eh * 60 + em) {
        out.push(`${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`);
        t += 60;
      }
    });
    return [...new Set(out)].sort();
  };

  const pickDate = async (d) => {
    setDate(d);
    setDayInfo(null);
    setMsg(null);
    if (!d) return;
    const { data } = await api.get(`/admin/week-availability?start=${d}`, { headers });
    setDayInfo(data.days[0]);
  };

  const schedule = async (t) => {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.post(`/admin/packs/${pack.id}/schedule`, { date, time: t }, { headers });
      setMsg({ ok: true, text: `Session locked in — ${fmtTime(t)}.` });
      pickDate(date);
      onChanged();
    } catch (ex) {
      setMsg({ ok: false, text: ex.response?.data?.detail || "Failed to schedule." });
    } finally {
      setBusy(false);
    }
  };

  const cancelSession = async (bid) => {
    if (!window.confirm("Cancel this pack session? The slot reopens and the session returns to the pack.")) return;
    await api.delete(`/admin/bookings/${bid}`, { headers });
    onChanged();
  };

  const togglePaid = async () => {
    await api.patch(`/admin/packs/${pack.id}`, { paid: !pack.paid }, { headers });
    onChanged();
  };

  const adjustDone = async (delta) => {
    const next = Math.min(10, Math.max(0, (pack.completed_sessions || 0) + delta));
    await api.patch(`/admin/packs/${pack.id}`, { completed_sessions: next }, { headers });
    onChanged();
  };

  const cancelPack = async () => {
    if (!window.confirm("Cancel this pack? Scheduled sessions stay unless you cancel them individually.")) return;
    await api.patch(`/admin/packs/${pack.id}`, { status: "cancelled" }, { headers });
    onChanged();
  };

  const times = dayInfo && !dayInfo.blocked ? genTimes(dayInfo.windows) : [];

  return (
    <div data-testid={`pack-card-${pack.id}`} className={`border border-white/10 p-6 ${cancelled ? "opacity-40" : ""}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="font-display uppercase text-xl tracking-tight">{pack.player_name}</span>
        <span className="text-white/50 text-sm">{pack.parent_name}{pack.phone ? ` · ${pack.phone}` : ""}{pack.email ? ` · ${pack.email}` : ""}</span>
        <span data-testid={`pack-used-${pack.id}`} className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-[#2a5fdb] text-[#2a5fdb]">
          {pack.used}/10 used
        </span>
        {!cancelled && (
          <span className="inline-flex items-center gap-1">
            <button
              data-testid={`pack-done-plus-${pack.id}`}
              onClick={() => adjustDone(1)}
              disabled={pack.left === 0}
              title="Mark one more session as done (played, not scheduled here)"
              className="h-7 px-2 border border-white/15 font-mono text-[10px] text-white/70 hover:border-[#2a5fdb] hover:text-[#2a5fdb] transition-colors disabled:opacity-30"
            >
              +1 done
            </button>
            <button
              data-testid={`pack-done-minus-${pack.id}`}
              onClick={() => adjustDone(-1)}
              disabled={(pack.completed_sessions || 0) === 0}
              title="Undo a marked-done session"
              className="h-7 px-2 border border-white/15 font-mono text-[10px] text-white/50 hover:border-white/40 transition-colors disabled:opacity-30"
            >
              −1
            </button>
          </span>
        )}
        <button
          data-testid={`pack-paid-toggle-${pack.id}`}
          onClick={togglePaid}
          className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border transition-colors ${
            pack.paid ? "border-emerald-400/50 text-emerald-400" : "border-red-400/40 text-red-400 hover:border-red-400"
          }`}
        >
          {pack.paid ? `Paid ${money(pack.price)}` : `Unpaid ${money(pack.price)} — tap when paid`}
        </button>
        <ExpiryBadge pack={pack} />
        {!cancelled && (
          <button data-testid={`pack-cancel-${pack.id}`} onClick={cancelPack} className="ml-auto text-white/40 hover:text-red-400 transition-colors" title="Cancel pack">
            <Trash2 size={16} />
          </button>
        )}
        {cancelled && <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] text-red-400">Cancelled</span>}
      </div>
      {pack.notes && <p className="mt-2 text-white/50 text-sm">{pack.notes}</p>}

      {pack.sessions.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {pack.sessions.map((s, i) => (
            <span key={s.id} className="inline-flex items-center gap-2 border border-white/15 px-3 py-1.5 font-mono text-[10px] tracking-[0.15em] text-white/80">
              #{i + 1} · {new Date(`${s.date}T${s.time}:00`).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })} {fmtTime(s.time)}
              <button data-testid={`pack-session-cancel-${s.id}`} onClick={() => cancelSession(s.id)} className="text-white/40 hover:text-red-400 transition-colors">×</button>
            </span>
          ))}
        </div>
      )}

      {!cancelled && pack.left > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">Schedule next:</span>
          <input data-testid={`pack-date-${pack.id}`} type="date" className={inputCls} value={date} onChange={(e) => pickDate(e.target.value)} />
          {dayInfo && times.length > 0 && times.map((t) => {
            const taken = dayInfo.booked_times.includes(t);
            return (
              <button
                key={t}
                data-testid={`pack-time-${pack.id}-${t}`}
                disabled={taken || busy}
                onClick={() => schedule(t)}
                className={`px-3 py-1.5 border font-mono text-[10px] tracking-[0.15em] transition-colors ${
                  taken ? "border-white/5 text-white/25 line-through cursor-not-allowed" : "border-[#2a5fdb] text-[#2a5fdb] hover:bg-[#2a5fdb] hover:text-white"
                }`}
              >
                {fmtTime(t)}
              </button>
            );
          })}
          {dayInfo && !dayInfo.blocked && times.length === 0 && (
            <span className="text-white/50 text-xs">No windows that day.</span>
          )}
          {dayInfo?.blocked && <span className="text-red-400 text-xs">Date is blocked.</span>}
        </div>
      )}
      {msg && (
        <p data-testid={`pack-msg-${pack.id}`} className={`mt-3 text-sm ${msg.ok ? "text-emerald-400" : "text-red-400"}`}>
          {msg.text}
        </p>
      )}
    </div>
  );
};

const RepTab = ({ headers }) => {
  const [items, setItems] = useState(null);
  const load = async () => {
    const { data } = await api.get("/admin/rep-signups", { headers });
    setItems(data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = async (id) => {
    if (!window.confirm("Cancel this signup? The spot will reopen.")) return;
    await api.delete(`/admin/rep-signups/${id}`, { headers });
    load();
  };

  const sendPayLink = async (s) => {
    const note = s.paid
      ? "This signup shows as paid from a TEST payment (no real money). Sending a link will mark it unpaid and email a fresh $150 card-payment link. Continue?"
      : `Email a $150 card-payment link to ${s.email}?`;
    if (!window.confirm(note)) return;
    try {
      await api.post(`/admin/rep-signups/${s.id}/send-pay-link`, {}, { headers });
      window.alert(`Payment link sent to ${s.email}.`);
      load();
    } catch (err) {
      window.alert(err.response?.data?.detail || "Could not send the payment link.");
    }
  };

  if (!items) return <p className="mt-10 text-white/50">Loading…</p>;
  const groups = { aspiring: "Next Step Rep (Wed 6:30–7:30am)" };
  return (
    <div className="mt-10 space-y-10">
      {Object.entries(groups).map(([key, label]) => {
        const rows = items.filter((s) => s.program === key);
        const active = rows.filter((s) => s.status === "confirmed").length;
        return (
          <div key={key}>
            <div className="flex items-baseline gap-4">
              <h3 className="font-display uppercase text-2xl tracking-tight">{label}</h3>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
                {active}/6 spots
              </span>
            </div>
            <div className="mt-4 border border-white/10 overflow-x-auto">
              <table data-testid={`rep-table-${key}`} className="w-full text-left text-sm">
                <thead className="bg-[#0a1428] font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                  <tr>
                    {["Player", "Born", "Parent", "Phone", "Email", "Notes", "Paid", "Status", ""].map((h, i) => (
                      <th key={i} className="px-4 py-3 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s) => (
                    <tr key={s.id} className={`border-t border-white/10 ${s.status === "cancelled" ? "opacity-40" : ""}`}>
                      <td className="px-4 py-4">{s.player_name}</td>
                      <td className="px-4 py-4 font-mono text-xs">{s.birth_year}</td>
                      <td className="px-4 py-4">{s.parent_name}</td>
                      <td className="px-4 py-4 whitespace-nowrap">{s.phone}</td>
                      <td className="px-4 py-4">{s.email}</td>
                      <td className="px-4 py-4 max-w-[200px] text-white/70">{s.notes || "—"}</td>
                      <td className="px-4 py-4 font-mono text-xs uppercase">
                        {s.paid ? <span className="text-emerald-400">paid</span> : "—"}
                      </td>
                      <td className="px-4 py-4 font-mono text-xs uppercase">{s.status}</td>
                      <td className="px-4 py-4">
                        {s.status === "confirmed" && (
                          <div className="flex items-center gap-3">
                            <button
                              data-testid={`rep-send-pay-link-${s.id}`}
                              onClick={() => sendPayLink(s)}
                              className="text-white/40 hover:text-[#2a5fdb] transition-colors"
                              title="Send $150 payment link by email"
                            >
                              <Send size={16} />
                            </button>
                            <button
                              data-testid={`rep-cancel-${s.id}`}
                              onClick={() => cancel(s.id)}
                              className="text-white/40 hover:text-red-400 transition-colors"
                              title="Cancel signup"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={9} className="p-6 text-center text-white/50 font-mono text-xs uppercase tracking-[0.2em]">No signups yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const BookingsTab = ({ headers }) => {
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [showReserve, setShowReserve] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [query, setQuery] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/bookings", { headers });
    setItems(data);
    const { data: s } = await api.get("/admin/stats", { headers });
    setStats(s);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = async (id) => {
    if (!window.confirm("Cancel this booking? The slot will reopen.")) return;
    await api.delete(`/admin/bookings/${id}`, { headers });
    load();
  };

  const PAYABLE = ["1-on-1", "10-pack", "group-10-pack"];
  const sendPayLink = async (b) => {
    const note = b.paid
      ? "This booking shows as paid from a TEST payment (no real money). Sending a link will mark it unpaid and email a fresh card-payment link. Continue?"
      : `Email a card-payment link to ${b.email}?`;
    if (!window.confirm(note)) return;
    try {
      await api.post(`/admin/bookings/${b.id}/send-pay-link`, {}, { headers });
      window.alert(`Payment link sent to ${b.email}.`);
      load();
    } catch (err) {
      window.alert(err.response?.data?.detail || "Could not send the payment link.");
    }
  };

  const togglePaid = async (b) => {
    await api.patch(`/admin/bookings/${b.id}`, { paid: !b.paid }, { headers });
    load();
  };

  if (!items) return <p className="mt-10 text-white/50">Loading…</p>;

  const today = new Date().toISOString().slice(0, 10);
  const q = query.trim().toLowerCase();
  const matches = (b) =>
    !q || [b.player_name, b.parent_name, b.phone, b.email].some((v) => (v || "").toLowerCase().includes(q));
  const upcoming = items
    .filter((b) => b.status === "confirmed" && b.date >= today && matches(b))
    .sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`));
  const past = items
    .filter((b) => !(b.status === "confirmed" && b.date >= today) && matches(b))
    .sort((a, b) => `${b.date}${b.time}`.localeCompare(`${a.date}${a.time}`));

  return (
    <>
      {stats && <StatsSnapshot stats={stats} />}

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <button
          data-testid="reserve-slot-toggle"
          onClick={() => setShowReserve((v) => !v)}
          className={`inline-flex items-center gap-2 border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
            showReserve ? "border-[#2a5fdb] text-[#2a5fdb]" : "border-white/15 text-white/70 hover:border-white/40"
          }`}
        >
          <CalendarPlus size={14} /> Reserve a slot
          <ChevronDown size={14} className={`transition-transform ${showReserve ? "rotate-180" : ""}`} />
        </button>
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="bookings-search-input"
            className={`${inputCls} w-full pl-9`}
            placeholder="Search player, parent, phone, email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        {q && (
          <button
            data-testid="bookings-search-clear"
            onClick={() => setQuery("")}
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 hover:text-white transition-colors"
          >
            Clear
          </button>
        )}
      </div>
      {showReserve && <ReserveSlotCard headers={headers} onReserved={load} />}

      <GroupRoster items={items} />

      <SectionHeading title="Upcoming" accent="bookings" count={upcoming.length} tid="bookings-upcoming-heading" />
      <BookingsTable rows={upcoming} onCancel={cancel} onSendPayLink={sendPayLink} onTogglePaid={togglePaid} payable={PAYABLE} empty="No upcoming bookings." tid="admin-bookings-table" />

      <div className="mt-10">
        <button
          data-testid="bookings-past-toggle"
          onClick={() => setShowPast((v) => !v)}
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-white transition-colors"
        >
          Past &amp; cancelled ({past.length})
          <ChevronDown size={14} className={`transition-transform ${showPast ? "rotate-180" : ""}`} />
        </button>
        {showPast && (
          <BookingsTable rows={past} onCancel={cancel} onSendPayLink={sendPayLink} onTogglePaid={togglePaid} payable={PAYABLE} empty="Nothing here yet." tid="admin-past-bookings-table" />
        )}
      </div>
    </>
  );
};

const SectionHeading = ({ title, accent, count, tid }) => (
  <h3 data-testid={tid} className="mt-10 font-display uppercase text-2xl tracking-tight">
    {title} <span className="text-[#2a5fdb]">{accent}</span>
    <span className="ml-3 align-middle font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 border border-white/15 px-2 py-1">{count}</span>
  </h3>
);

const BookingsTable = ({ rows, onCancel, onSendPayLink, onTogglePaid, payable, empty, tid }) => (
  <div className="mt-4 border border-white/10 overflow-x-auto">
    <table data-testid={tid} className="w-full text-left text-sm">
      <thead className="bg-[#0a1428] font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
        <tr>
          {["When", "Player", "Contact", "Session", "Notes", "Status", ""].map((h, i) => (
            <th key={i} className="px-4 py-3 whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((b) => (
          <tr key={b.id} className={`border-t border-white/10 ${b.status === "cancelled" ? "opacity-40" : ""}`}>
            <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">
              {new Date(`${b.date}T${b.time}:00`).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })}
              <div className="text-white/50 mt-0.5">{fmtTime(b.time)}</div>
            </td>
            <td className="px-4 py-3">
              {b.player_name}
              {b.parent_name && b.parent_name !== b.player_name && (
                <div className="text-white/40 text-xs mt-0.5">{b.parent_name}</div>
              )}
            </td>
            <td className="px-4 py-3 whitespace-nowrap text-xs">
              <div>{b.phone || "—"}</div>
              <div className="text-white/40 mt-0.5">{b.email || ""}</div>
            </td>
            <td className="px-4 py-3 whitespace-nowrap font-mono text-xs uppercase tracking-wide">{b.session_type}</td>
            <td className="px-4 py-3 max-w-[220px] text-white/70 text-xs">{b.notes || "—"}</td>
            <td className="px-4 py-3 font-mono text-xs uppercase whitespace-nowrap">
              {b.status}
              {b.paid && <span className="text-emerald-400"> · paid</span>}
              {b.session_type === "reserved" && b.price_cents && !b.paid ? (
                <span className="text-red-400"> · owes ${(b.price_cents / 100).toLocaleString("en-AU")}</span>
              ) : null}
            </td>
            <td className="px-4 py-3">
              {b.status === "confirmed" && (
                <div className="flex items-center gap-3">
                  {b.session_type === "reserved" && b.price_cents ? (
                    <button
                      data-testid={`admin-toggle-paid-${b.id}`}
                      onClick={() => onTogglePaid(b)}
                      className={`transition-colors ${b.paid ? "text-emerald-400 hover:text-white/40" : "text-white/40 hover:text-emerald-400"}`}
                      title={b.paid ? "Mark as unpaid" : "Mark as paid (cash/transfer)"}
                    >
                      <DollarSign size={16} />
                    </button>
                  ) : null}
                  {payable.includes(b.session_type) && b.email && (
                    <button
                      data-testid={`admin-send-pay-link-${b.id}`}
                      onClick={() => onSendPayLink(b)}
                      className="text-white/40 hover:text-[#2a5fdb] transition-colors"
                      title="Send payment link by email"
                    >
                      <Send size={16} />
                    </button>
                  )}
                  <button
                    data-testid={`admin-cancel-booking-${b.id}`}
                    onClick={() => onCancel(b.id)}
                    className="text-white/40 hover:text-red-400 transition-colors"
                    title="Cancel booking"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </td>
          </tr>
        ))}
        {rows.length === 0 && (
          <tr><td colSpan={7} className="p-8 text-center text-white/50 font-mono text-xs uppercase tracking-[0.2em]">{empty}</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

const GROUP_CAPACITY = 8;

const PRICE_OPTIONS = [
  { key: "1on1", label: "1-on-1 · $100", cents: 10000 },
  { key: "group", label: "Group · $50", cents: 5000 },
  { key: "custom", label: "Custom $", cents: null },
  { key: "free", label: "No charge", cents: 0 },
];

const ReserveSlotCard = ({ headers, onReserved }) => {
  const [date, setDate] = useState("");
  const [dayInfo, setDayInfo] = useState(null);
  const [time, setTime] = useState(null);
  const [note, setNote] = useState("");
  const [priceKey, setPriceKey] = useState("1on1");
  const [customAmount, setCustomAmount] = useState("");
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // {ok, text}

  const genTimes = (windows) => {
    const out = [];
    windows.forEach((w) => {
      const [sh, sm] = w.start.split(":").map(Number);
      const [eh, em] = w.end.split(":").map(Number);
      let t = sh * 60 + sm;
      while (t + 60 <= eh * 60 + em) {
        out.push(`${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`);
        t += 60;
      }
    });
    return [...new Set(out)].sort();
  };

  const loadDay = async (d) => {
    const { data } = await api.get(`/admin/week-availability?start=${d}`, { headers });
    setDayInfo(data.days[0]);
  };

  const pickDate = async (d) => {
    setDate(d);
    setTime(null);
    setMsg(null);
    setDayInfo(null);
    if (!d) return;
    loadDay(d);
  };

  const priceCents = () => {
    if (priceKey === "custom") return Math.round(Number(customAmount || 0) * 100);
    return PRICE_OPTIONS.find((o) => o.key === priceKey)?.cents ?? 0;
  };

  const reserve = async (e) => {
    e.preventDefault();
    if (!date || !time || busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const cents = priceCents();
      await api.post(
        "/admin/reserve",
        { date, time, note: note || null, price_cents: cents || null, paid: alreadyPaid && cents > 0 },
        { headers }
      );
      setMsg({ ok: true, text: `Reserved ${fmtTime(time)} — it now shows as Booked on the site.` });
      setNote("");
      setTime(null);
      setCustomAmount("");
      setAlreadyPaid(false);
      loadDay(date);
      onReserved();
    } catch (ex) {
      setMsg({ ok: false, text: ex.response?.data?.detail || "Failed to reserve." });
    } finally {
      setBusy(false);
    }
  };

  const times = dayInfo && !dayInfo.blocked ? genTimes(dayInfo.windows) : [];

  return (
    <div data-testid="reserve-slot-card" className="mt-10 border border-white/10 bg-[#0a1428] p-6">
      <h3 className="font-display uppercase text-2xl tracking-tight">
        Reserve <span className="text-[#2a5fdb]">a slot</span>
      </h3>
      <p className="mt-2 text-white/60 text-sm leading-relaxed max-w-2xl">
        Client booked by text or call? Mark the slot here — it instantly shows
        as Booked on the site. No emails are sent. Cancel it anytime from the
        table below to reopen it.
      </p>
      <form onSubmit={reserve} className="mt-5 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
            Date
            <input
              data-testid="reserve-date-input"
              type="date"
              className={inputCls}
              value={date}
              onChange={(e) => pickDate(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 flex-1 min-w-[200px]">
            Client (optional)
            <input
              data-testid="reserve-note-input"
              className={inputCls}
              placeholder="e.g. Jake M"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
        </div>
        {date && dayInfo && (
          <div>
            {dayInfo.blocked ? (
              <p className="text-red-400 text-sm">That date is blocked.</p>
            ) : times.length === 0 ? (
              <p className="text-white/50 text-sm">
                No availability windows that day — add one in the Availability tab first.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {times.map((t) => {
                  const taken = dayInfo.booked_times.includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      data-testid={`reserve-time-${t}`}
                      disabled={taken}
                      onClick={() => setTime(t)}
                      className={`px-4 py-2 border font-mono text-xs tracking-[0.15em] transition-colors ${
                        time === t
                          ? "border-[#2a5fdb] bg-[#2a5fdb] text-white"
                          : taken
                          ? "border-white/5 text-white/25 line-through cursor-not-allowed"
                          : "border-white/15 text-white/80 hover:border-white/40"
                      }`}
                    >
                      {fmtTime(t)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Price</div>
          <div className="flex flex-wrap items-center gap-2">
            {PRICE_OPTIONS.map((o) => (
              <button
                key={o.key}
                type="button"
                data-testid={`reserve-price-${o.key}`}
                onClick={() => setPriceKey(o.key)}
                className={`px-4 py-2 border font-mono text-xs tracking-[0.15em] transition-colors ${
                  priceKey === o.key
                    ? "border-[#2a5fdb] bg-[#2a5fdb] text-white"
                    : "border-white/15 text-white/80 hover:border-white/40"
                }`}
              >
                {o.label}
              </button>
            ))}
            {priceKey === "custom" && (
              <input
                data-testid="reserve-custom-amount"
                type="number"
                min="1"
                step="1"
                className={`${inputCls} w-28`}
                placeholder="AUD"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
              />
            )}
          </div>
          {priceKey !== "free" && (
            <label className="mt-3 flex items-center gap-2 text-sm text-white/70 cursor-pointer w-fit">
              <input
                data-testid="reserve-paid-checkbox"
                type="checkbox"
                checked={alreadyPaid}
                onChange={(e) => setAlreadyPaid(e.target.checked)}
                className="accent-[#2a5fdb] w-4 h-4"
              />
              Already paid (cash / bank transfer) — count it in earnings
            </label>
          )}
        </div>
        {msg && (
          <p
            data-testid="reserve-result"
            className={`text-sm ${msg.ok ? "text-emerald-400" : "text-red-400"}`}
          >
            {msg.text}
          </p>
        )}
        <button
          data-testid="reserve-submit"
          type="submit"
          disabled={!date || !time || busy}
          className="btn-sharp disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? "Reserving…" : "Reserve slot"}
        </button>
      </form>
    </div>
  );
};

const GroupRoster = ({ items }) => {
  const today = new Date().toISOString().slice(0, 10);
  const groups = {};
  items
    .filter(
      (b) =>
        b.status === "confirmed" &&
        b.session_type === "group-10-pack" &&
        b.date >= today
    )
    .forEach((b) => {
      const k = `${b.date}|${b.time}`;
      (groups[k] = groups[k] || []).push(b);
    });
  const keys = Object.keys(groups).sort();
  if (keys.length === 0) return null;

  return (
    <div data-testid="group-roster" className="mt-10">
      <h3 className="font-display uppercase text-2xl tracking-tight">
        Group <span className="text-[#2a5fdb]">sessions</span>
      </h3>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        {keys.map((k) => {
          const [date, time] = k.split("|");
          const players = groups[k];
          const priv = players.length === 1 && players[0].private_group;
          const full = players.length >= GROUP_CAPACITY;
          return (
            <div
              key={k}
              data-testid={`group-slot-${date}-${time}`}
              className="border border-white/10 bg-[#0a1428] p-5"
            >
              <div className="flex items-center justify-between gap-4">
                <span className="font-display uppercase text-lg tracking-tight">
                  {new Date(`${date}T${time}:00`).toLocaleDateString("en-AU", {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                  })}{" "}
                  · {fmtTime(time)}
                </span>
                <span
                  data-testid={`group-slot-count-${date}-${time}`}
                  className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${
                    priv
                      ? "border-[#2a5fdb] text-[#2a5fdb]"
                      : full
                      ? "border-red-400/50 text-red-400"
                      : "border-emerald-400/40 text-emerald-400"
                  }`}
                >
                  {priv
                    ? `Private · ${players[0].group_size} players`
                    : full
                    ? "FULL"
                    : `${players.length}/${GROUP_CAPACITY}`}
                </span>
              </div>
              <ul className="mt-4 space-y-2">
                {players.map((b) => (
                  <li
                    key={b.id}
                    className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm"
                  >
                    <span className="text-white">{b.player_name}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">
                      {b.parent_name} · {b.phone}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StatsSnapshot = ({ stats }) => {
  const [showMore, setShowMore] = useState(false);
  const maxDay = Math.max(1, ...stats.per_weekday.map((d) => d.count));
  const maxWeek = Math.max(1, ...stats.recent_weeks.map((w) => w.count));
  const maxRev = Math.max(1, ...stats.recent_weeks.map((w) => w.revenue || 0));
  const money = (c) => `$${((c || 0) / 100).toLocaleString("en-AU")}`;
  return (
    <div data-testid="admin-stats" className="mt-10">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="This week" value={stats.this_week} tid="stat-this-week" />
        <StatCard label="Upcoming" value={stats.upcoming} tid="stat-upcoming" />
        <StatCard label="Earned this week" value={money(stats.revenue_this_week)} tid="stat-revenue-week" accent />
        <StatCard label="Total earned" value={money(stats.revenue_total)} tid="stat-revenue-total" accent />
        <StatCard label="Unpaid (upcoming)" value={money(stats.outstanding)} tid="stat-outstanding" />
      </div>
      <button
        data-testid="stats-more-toggle"
        onClick={() => setShowMore((v) => !v)}
        className="mt-3 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-white transition-colors"
      >
        {showMore ? "Hide charts" : "More stats & charts"}
        <ChevronDown size={14} className={`transition-transform ${showMore ? "rotate-180" : ""}`} />
      </button>
      {showMore && (
        <>
      <div className="mt-3 grid grid-cols-2 gap-3 md:max-w-md">
        <StatCard label="Busiest day" value={stats.busiest_day || "—"} tid="stat-busiest-day" />
        <StatCard
          label="Busiest time"
          value={stats.busiest_time ? fmtTime(stats.busiest_time) : "—"}
          tid="stat-busiest-time"
        />
      </div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="border border-white/10 bg-[#0a1428] p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">
            Sessions per week (last 6)
          </div>
          <div className="flex items-end gap-2 h-24">
            {stats.recent_weeks.map((w) => (
              <div key={w.week_start} className="flex-1 flex flex-col items-center gap-2">
                <span className="font-mono text-[10px] text-white/70">{w.count}</span>
                <div
                  className="w-full bg-[#2a5fdb]"
                  style={{ height: `${Math.max(4, (w.count / maxWeek) * 70)}px` }}
                />
                <span className="font-mono text-[8px] uppercase tracking-wide text-white/40">
                  {w.label}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-white/10 bg-[#0a1428] p-5" data-testid="earnings-chart">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">
            Earnings per week (last 6)
          </div>
          <div className="flex items-end gap-2 h-24">
            {stats.recent_weeks.map((w) => (
              <div key={w.week_start} className="flex-1 flex flex-col items-center gap-2">
                <span className="font-mono text-[9px] text-emerald-400">
                  {w.revenue ? money(w.revenue) : "—"}
                </span>
                <div
                  className="w-full bg-emerald-400/70"
                  style={{ height: `${Math.max(4, ((w.revenue || 0) / maxRev) * 70)}px` }}
                />
                <span className="font-mono text-[8px] uppercase tracking-wide text-white/40">
                  {w.label}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-white/10 bg-[#0a1428] p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">
            Sessions by day
          </div>
          <div className="flex items-end gap-2 h-24">
            {stats.per_weekday.map((d) => (
              <div key={d.day} className="flex-1 flex flex-col items-center gap-2">
                <span className="font-mono text-[10px] text-white/70">{d.count}</span>
                <div
                  className="w-full bg-white/25"
                  style={{ height: `${Math.max(4, (d.count / maxDay) * 70)}px` }}
                />
                <span className="font-mono text-[8px] uppercase tracking-wide text-white/40">
                  {d.day}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

const StatCard = ({ label, value, tid, accent }) => (
  <div data-testid={tid} className="border border-white/10 bg-[#0a1428] p-5">
    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">{label}</div>
    <div
      className={`font-display uppercase text-3xl md:text-4xl mt-2 tracking-tight ${
        accent ? "text-emerald-400" : "text-white"
      }`}
    >
      {value}
    </div>
  </div>
);

const AvailabilityTab = ({ headers }) => {
  const [rules, setRules] = useState(null);
  const [form, setForm] = useState({ day: 0, start: "16:00", end: "19:00" });
  const [err, setErr] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/availability", { headers });
    setRules(data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/admin/availability", { ...form, day: Number(form.day) }, { headers });
      load();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Failed to add.");
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/availability/${id}`, { headers });
    load();
  };

  return (
    <div className="mt-10 max-w-4xl">
      <WeekPlanner headers={headers} />

      <div className="mt-14 pt-10 border-t border-white/10">
        <h3 className="font-display uppercase text-2xl tracking-tight">
          Weekly <span className="text-[#2a5fdb]">defaults</span>
        </h3>
        <p className="mt-2 text-white/60 text-sm leading-relaxed">
          Every week starts from these hours. Use the planner above to change
          specific dates. Slots are offered in 60-minute blocks and booked times
          disappear automatically.
        </p>
      <form onSubmit={add} className="mt-6 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
          Day
          <select
            data-testid="admin-avail-day"
            className={inputCls}
            value={form.day}
            onChange={(e) => setForm({ ...form, day: e.target.value })}
          >
            {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
          From
          <input data-testid="admin-avail-start" type="time" step="3600" className={inputCls} value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} />
        </label>
        <label className="flex flex-col gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
          Until
          <input data-testid="admin-avail-end" type="time" step="3600" className={inputCls} value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} />
        </label>
        <button data-testid="admin-avail-add" type="submit" className="btn-sharp">Add window</button>
      </form>
      {err && <p className="mt-3 text-red-400 text-sm">{err}</p>}

      {!rules ? (
        <p className="mt-8 text-white/50">Loading…</p>
      ) : (
        <div className="mt-8 border border-white/10 divide-y divide-white/10">
          {rules.map((r) => (
            <div key={r.id} className="flex items-center justify-between px-5 py-4">
              <div className="flex items-baseline gap-4">
                <span className="font-display uppercase text-lg tracking-tight w-32">{DAYS[r.day]}</span>
                <span className="font-mono text-xs text-white/70 tracking-[0.15em]">
                  {fmtTime(r.start)} — {fmtTime(r.end)}
                </span>
              </div>
              <button
                data-testid={`admin-avail-delete-${r.id}`}
                onClick={() => remove(r.id)}
                className="text-white/40 hover:text-red-400 transition-colors"
                title="Remove window"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {rules.length === 0 && (
            <p className="p-8 text-center text-white/50 font-mono text-xs uppercase tracking-[0.2em]">
              No windows yet — add one above so parents can book.
            </p>
          )}
        </div>
      )}
      </div>
      <BlockedDatesSection headers={headers} />
    </div>
  );
};

const WeekPlanner = ({ headers }) => {
  const mondayOf = (d) => {
    const dt = new Date(d);
    dt.setDate(dt.getDate() - ((dt.getDay() + 6) % 7));
    dt.setHours(0, 0, 0, 0);
    return dt;
  };
  const iso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [days, setDays] = useState(null);

  const load = async (ws) => {
    const { data } = await api.get(`/admin/week-availability?start=${iso(ws)}`, { headers });
    setDays(data.days);
  };
  useEffect(() => { load(weekStart); }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  const shift = (weeks) => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + weeks * 7);
    if (next < mondayOf(new Date())) return;
    setWeekStart(next);
    setDays(null);
  };

  const saveWindows = async (date, windows) => {
    await api.put("/admin/overrides", { date, windows }, { headers });
    load(weekStart);
  };

  const resetDay = async (date) => {
    await api.delete(`/admin/overrides/${date}`, { headers });
    load(weekStart);
  };

  const isCurrentWeek = iso(weekStart) === iso(mondayOf(new Date()));
  const weekLabel = weekStart.toLocaleDateString("en-AU", { day: "numeric", month: "long" });

  return (
    <div data-testid="week-planner">
      <h3 className="font-display uppercase text-2xl tracking-tight">
        Week by <span className="text-[#2a5fdb]">week</span>
      </h3>
      <p className="mt-2 text-white/60 text-sm leading-relaxed max-w-2xl">
        Fine-tune any specific date — different hours, extra windows, or a day
        off. Days you haven't touched follow your weekly defaults below.
      </p>

      <div className="mt-6 flex items-center gap-4">
        <button
          data-testid="week-prev"
          onClick={() => shift(-1)}
          disabled={isCurrentWeek}
          className="h-10 w-10 border border-white/15 flex items-center justify-center text-white/70 hover:border-white/40 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          ←
        </button>
        <span data-testid="week-label" className="font-mono text-xs uppercase tracking-[0.2em] text-white">
          Week of {weekLabel}
          {isCurrentWeek && <span className="text-[#2a5fdb]"> · this week</span>}
        </span>
        <button
          data-testid="week-next"
          onClick={() => shift(1)}
          className="h-10 w-10 border border-white/15 flex items-center justify-center text-white/70 hover:border-white/40 hover:text-white transition-colors"
        >
          →
        </button>
      </div>

      {!days ? (
        <p className="mt-6 text-white/50">Loading…</p>
      ) : (
        <div className="mt-6 border border-white/10 divide-y divide-white/10">
          {days.map((d) => (
            <DayRow key={d.date} day={d} onSave={saveWindows} onReset={resetDay} />
          ))}
        </div>
      )}
    </div>
  );
};

const DayRow = ({ day, onSave, onReset }) => {
  const [start, setStart] = useState("16:00");
  const [end, setEnd] = useState("19:00");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const past = day.date < new Date().toISOString().slice(0, 10);

  const run = async (fn) => {
    setBusy(true);
    setErr("");
    try {
      await fn();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Failed to save.");
    } finally {
      setBusy(false);
    }
  };

  const addWindow = () =>
    run(() => onSave(day.date, [...day.windows, { start, end }]));
  const removeWindow = (idx) =>
    run(() => onSave(day.date, day.windows.filter((_, i) => i !== idx)));
  const dayOff = () => run(() => onSave(day.date, []));
  const reset = () => run(() => onReset(day.date));

  return (
    <div
      data-testid={`day-row-${day.date}`}
      className={`px-5 py-4 ${past ? "opacity-40 pointer-events-none" : ""}`}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="w-36 shrink-0">
          <span className="font-display uppercase text-lg tracking-tight">
            {day.day_name.slice(0, 3)} {day.day_num} {day.month}
          </span>
        </div>
        <span
          className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 border ${
            day.blocked
              ? "border-red-400/40 text-red-400"
              : day.source === "custom"
              ? "border-[#2a5fdb] text-[#2a5fdb]"
              : "border-white/15 text-white/40"
          }`}
        >
          {day.blocked ? "Blocked" : day.source === "custom" ? "Custom" : "Default"}
        </span>
        {day.booked_times.length > 0 && (
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400">
            {day.booked_times.length} booked
          </span>
        )}

        <div className="flex flex-wrap items-center gap-2 flex-1">
          {day.windows.map((w, i) => (
            <span
              key={`${w.start}-${w.end}`}
              className="inline-flex items-center gap-2 border border-white/15 px-3 py-1.5 font-mono text-[10px] tracking-[0.15em] text-white/80"
            >
              {fmtTime(w.start)}–{fmtTime(w.end)}
              <button
                data-testid={`day-window-remove-${day.date}-${i}`}
                onClick={() => removeWindow(i)}
                disabled={busy}
                className="text-white/40 hover:text-red-400 transition-colors"
              >
                ×
              </button>
            </span>
          ))}
          {day.windows.length === 0 && !day.blocked && (
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">
              No sessions
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <input
            data-testid={`day-start-${day.date}`}
            type="time"
            step="3600"
            className="bg-[#0a1428] border border-white/15 px-2 py-1.5 text-xs text-white focus:outline-none focus:border-[#2a5fdb]"
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
          <span className="text-white/40 text-xs">–</span>
          <input
            data-testid={`day-end-${day.date}`}
            type="time"
            step="3600"
            className="bg-[#0a1428] border border-white/15 px-2 py-1.5 text-xs text-white focus:outline-none focus:border-[#2a5fdb]"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
          <button
            data-testid={`day-add-window-${day.date}`}
            onClick={addWindow}
            disabled={busy}
            className="border border-[#2a5fdb] text-[#2a5fdb] hover:bg-[#2a5fdb] hover:text-white transition-colors px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em]"
          >
            + Add
          </button>
          {day.windows.length > 0 && (
            <button
              data-testid={`day-off-${day.date}`}
              onClick={dayOff}
              disabled={busy}
              className="text-white/40 hover:text-red-400 transition-colors font-mono text-[10px] uppercase tracking-[0.15em] px-2"
            >
              Day off
            </button>
          )}
          {day.source === "custom" && (
            <button
              data-testid={`day-reset-${day.date}`}
              onClick={reset}
              disabled={busy}
              className="text-white/40 hover:text-white transition-colors font-mono text-[10px] uppercase tracking-[0.15em] px-2"
            >
              Reset
            </button>
          )}
        </div>
      </div>
      {err && <p className="mt-2 text-red-400 text-xs">{err}</p>}
    </div>
  );
};

const BlockedDatesSection = ({ headers }) => {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ start_date: "", end_date: "", reason: "" });
  const [err, setErr] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/blocked-dates", { headers });
    setItems(data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (e) => {
    e.preventDefault();
    if (!form.start_date) return;
    setErr("");
    try {
      await api.post(
        "/admin/blocked-dates",
        {
          start_date: form.start_date,
          end_date: form.end_date || null,
          reason: form.reason || null,
        },
        { headers }
      );
      setForm({ start_date: "", end_date: "", reason: "" });
      load();
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Failed to block date.");
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/blocked-dates/${id}`, { headers });
    load();
  };

  const fmtDate = (ds) =>
    new Date(`${ds}T00:00:00`).toLocaleDateString("en-AU", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  return (
    <div className="mt-14 pt-10 border-t border-white/10">
      <h3 className="font-display uppercase text-2xl tracking-tight">
        Blocked <span className="text-[#2a5fdb]">dates</span>
      </h3>
      <p className="mt-2 text-white/60 text-sm leading-relaxed">
        Block a single day or a whole period (e.g. 1–19 Dec for holidays) —
        those days won't be bookable. Leave "Until" empty to block just one day.
      </p>
      <form onSubmit={add} className="mt-6 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
          From
          <input
            data-testid="admin-blocked-date-input"
            type="date"
            className={inputCls}
            value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
          />
        </label>
        <label className="flex flex-col gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
          Until (optional)
          <input
            data-testid="admin-blocked-end-input"
            type="date"
            className={inputCls}
            value={form.end_date}
            onChange={(e) => setForm({ ...form, end_date: e.target.value })}
          />
        </label>
        <label className="flex flex-col gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
          Reason (optional)
          <input
            data-testid="admin-blocked-reason-input"
            className={inputCls}
            placeholder="e.g. Holidays"
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
          />
        </label>
        <button data-testid="admin-blocked-add" type="submit" className="btn-sharp">
          Block
        </button>
      </form>
      {err && <p className="mt-3 text-red-400 text-sm">{err}</p>}

      {!items ? (
        <p className="mt-6 text-white/50">Loading…</p>
      ) : (
        <div className="mt-6 border border-white/10 divide-y divide-white/10">
          {items.map((b) => (
            <div key={b.id} className="flex items-center justify-between px-5 py-4">
              <div className="flex items-baseline gap-4 flex-wrap">
                <span className="font-mono text-xs text-white tracking-[0.15em]">
                  {b.start_date === b.end_date
                    ? fmtDate(b.start_date)
                    : `${fmtDate(b.start_date)} — ${fmtDate(b.end_date)}`}
                </span>
                {b.reason && <span className="text-white/50 text-sm">{b.reason}</span>}
              </div>
              <button
                data-testid={`admin-blocked-delete-${b.id}`}
                onClick={() => remove(b.id)}
                className="text-white/40 hover:text-red-400 transition-colors"
                title="Unblock"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {items.length === 0 && (
            <p className="p-8 text-center text-white/50 font-mono text-xs uppercase tracking-[0.2em]">
              No blocked dates.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const WaitlistTab = ({ headers }) => {
  const [items, setItems] = useState(null);
  const [blast, setBlast] = useState({ subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [blastMsg, setBlastMsg] = useState(null); // {ok, text}

  useEffect(() => {
    api
      .get("/admin/waitlist", { headers })
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendBlast = async (e) => {
    e.preventDefault();
    if (!blast.subject.trim() || !blast.message.trim() || sending) return;
    if (!window.confirm(`Send this email to all ${items?.length || 0} people on the waitlist?`)) return;
    setSending(true);
    setBlastMsg(null);
    try {
      const { data } = await api.post("/admin/waitlist/blast", blast, { headers });
      setBlastMsg({ ok: true, text: `Sending to ${data.recipients} parent${data.recipients === 1 ? "" : "s"} — emails are going out now.` });
      setBlast({ subject: "", message: "" });
    } catch (ex) {
      setBlastMsg({ ok: false, text: ex.response?.data?.detail || "Failed to send." });
    } finally {
      setSending(false);
    }
  };

  if (!items) return <p className="mt-10 text-white/50">Loading…</p>;
  return (
    <div className="mt-10 max-w-3xl">
      <div className="border border-white/10 bg-[#0a1428] p-6 md:p-8">
        <h3 className="font-display uppercase text-2xl tracking-tight">
          Email <span className="text-[#2a5fdb]">everyone</span>
        </h3>
        <p className="mt-2 text-white/60 text-sm leading-relaxed">
          When 2027 camp dates lock in, write it once and it goes to all{" "}
          {items.length} waitlist parent{items.length === 1 ? "" : "s"}. Each
          email is personalised with their name.
        </p>
        <form onSubmit={sendBlast} className="mt-5 space-y-3">
          <input
            data-testid="blast-subject-input"
            className={`${inputCls} w-full py-3 px-4`}
            placeholder="Subject — e.g. C2C Holiday Camp dates are locked in!"
            value={blast.subject}
            onChange={(e) => setBlast({ ...blast, subject: e.target.value })}
          />
          <textarea
            data-testid="blast-message-input"
            className={`${inputCls} w-full py-3 px-4 min-h-[130px] resize-y`}
            placeholder={"Your message — e.g. Camps run 12–15 January at Randwick. Spots are limited, reply to lock yours in."}
            value={blast.message}
            onChange={(e) => setBlast({ ...blast, message: e.target.value })}
          />
          {blastMsg && (
            <p
              data-testid="blast-result"
              className={`text-sm ${blastMsg.ok ? "text-emerald-400" : "text-red-400"}`}
            >
              {blastMsg.text}
            </p>
          )}
          <button
            data-testid="blast-send-btn"
            type="submit"
            disabled={!blast.subject.trim() || !blast.message.trim() || sending || items.length === 0}
            className="btn-sharp disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {sending ? "Sending…" : `Send to all (${items.length})`}
            <span aria-hidden>→</span>
          </button>
        </form>
      </div>

      <p className="mt-10 text-white/60 text-sm leading-relaxed">
        Parents registered for the 2027 holiday camps waitlist.
      </p>
      <div className="mt-6 border border-white/10 overflow-x-auto">
        <table data-testid="admin-waitlist-table" className="w-full text-left text-sm">
          <thead className="bg-[#0a1428] font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
            <tr>
              {["Joined", "Parent", "Email"].map((h) => (
                <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((w) => (
              <tr key={w.id} className="border-t border-white/10">
                <td className="px-4 py-4 font-mono text-xs whitespace-nowrap">
                  {new Date(w.created_at).toLocaleDateString("en-AU")}
                </td>
                <td className="px-4 py-4">{w.parent_name}</td>
                <td className="px-4 py-4">{w.email}</td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={3} className="p-8 text-center text-white/50 font-mono text-xs uppercase tracking-[0.2em]">
                  No signups yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const playerAge = (dob) => {
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  if (now < new Date(now.getFullYear(), d.getMonth(), d.getDate())) age -= 1;
  return age;
};

const RequestsTab = ({ headers }) => {
  const [items, setItems] = useState(null);
  const load = async () => {
    const { data } = await api.get("/admin/booking-requests", { headers });
    setItems(data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleContacted = async (r) => {
    await api.patch(`/admin/booking-requests/${r.id}`, { contacted: !r.contacted }, { headers });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this booking request?")) return;
    await api.delete(`/admin/booking-requests/${id}`, { headers });
    load();
  };

  if (!items) return <p className="mt-10 text-white/50">Loading…</p>;
  return (
    <div className="mt-10">
      <p className="text-white/50 text-sm max-w-xl">
        Booking requests from the website form — text the parent to lock in
        session times, then tick them off as contacted.
      </p>
      <div className="mt-6 border border-white/10 overflow-x-auto">
        <table data-testid="admin-requests-table" className="w-full text-left text-sm">
          <thead className="bg-[#0a1428] font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
            <tr>
              {["Received", "Player", "DOB", "Parent", "Contact", "Level", "Status", ""].map((h, i) => (
                <th key={i} className="px-4 py-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className={`border-t border-white/10 ${r.contacted ? "opacity-50" : ""}`}>
                <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">
                  {new Date(r.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
                </td>
                <td className="px-4 py-3">{r.player_name}</td>
                <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">
                  {r.player_dob}
                  <span className="text-white/40"> ({playerAge(r.player_dob)}yo)</span>
                </td>
                <td className="px-4 py-3">{r.parent_name}</td>
                <td className="px-4 py-3 whitespace-nowrap text-xs">
                  <a href={`sms:${r.phone}`} className="text-[#2a5fdb] hover:underline">{r.phone}</a>
                  <div className="text-white/40 mt-0.5">{r.email}</div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap font-mono text-xs uppercase tracking-wide">{r.level}</td>
                <td className="px-4 py-3 font-mono text-xs uppercase whitespace-nowrap">
                  {r.contacted ? <span className="text-emerald-400">contacted</span> : <span className="text-amber-400">new</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <button
                      data-testid={`request-contacted-${r.id}`}
                      onClick={() => toggleContacted(r)}
                      className={`transition-colors ${r.contacted ? "text-emerald-400 hover:text-white/40" : "text-white/40 hover:text-emerald-400"}`}
                      title={r.contacted ? "Mark as new" : "Mark as contacted"}
                    >
                      <CheckCircle2 size={16} />
                    </button>
                    <button
                      data-testid={`request-delete-${r.id}`}
                      onClick={() => remove(r.id)}
                      className="text-white/40 hover:text-red-400 transition-colors"
                      title="Delete request"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-white/50 font-mono text-xs uppercase tracking-[0.2em]">No booking requests yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const InquiriesTab = () => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/inquiries").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);
  if (!items) return <p className="mt-10 text-white/50">Loading…</p>;
  return (
    <div className="mt-10 border border-white/10 overflow-x-auto">
      <table data-testid="admin-table" className="w-full text-left text-sm">
        <thead className="bg-[#0a1428] font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
          <tr>
            {["Received", "Parent", "Email", "Phone", "Child", "Age", "Program", "Message"].map((h) => (
              <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className="border-t border-white/10">
              <td className="px-4 py-4 font-mono text-xs">{new Date(it.created_at).toLocaleString("en-AU")}</td>
              <td className="px-4 py-4">{it.parent_name}</td>
              <td className="px-4 py-4">{it.email}</td>
              <td className="px-4 py-4">{it.phone || "—"}</td>
              <td className="px-4 py-4">{it.child_name || "—"}</td>
              <td className="px-4 py-4 font-mono text-xs">{it.child_age}</td>
              <td className="px-4 py-4">{it.program}</td>
              <td className="px-4 py-4 max-w-[280px] text-white/70">{it.message || "—"}</td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan={8} className="p-8 text-center text-white/50 font-mono text-xs uppercase tracking-[0.2em]">No inquiries yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
